import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import type { ApercuCorrespondance, ErreurRegle } from '../../../../lib/correspondance.js';
import { TableauDefilant } from '../../../../components/TableauDefilant.js';
import tableStyles from '../../../../styles/table.module.css';
import reprise from '../Reprise.module.css';
import styles from './Correspondance.module.css';

const LIGNES_AFFICHEES = 5;
const ERREURS_AFFICHEES = 12;

/**
 * L'aperçu en direct : les règles appliquées à l'échantillon du profil, rien
 * d'écrit. Par nature, ce qui est produit, écarté (doublons, vides, filtres)
 * ou refusé ; puis les erreurs par ligne de la source. Ce sont les seules
 * lignes de données que l'écran montre, avec le profil et les rapports.
 */
export function ApercuVue({
  apercu,
  chargement,
  refusees,
  erreur,
}: {
  apercu: ApercuCorrespondance | undefined;
  chargement: boolean;
  /** L'API a refusé les règles (400) : les champs concernés, et pourquoi. */
  refusees: readonly ErreurRegle[];
  erreur: string | null;
}): JSX.Element {
  const { t } = useTranslation('console');
  const natures = Object.entries(apercu?.natures ?? {});

  return (
    <section className={styles.apercu} aria-labelledby="corr-apercu" aria-busy={chargement}>
      <div className={reprise.rangee}>
        <h3 id="corr-apercu" className={reprise.sousTitre}>
          {t('correspondance.apercu.titre')}
        </h3>
        <span className={reprise.discret} aria-live="polite">
          {chargement ? t('correspondance.apercu.calcul') : t('correspondance.apercu.aJour')}
        </span>
      </div>
      <p className={reprise.discret}>{t('correspondance.apercu.texte')}</p>

      {refusees.length > 0 ? (
        <p className={reprise.avertissement} role="status">
          {t('correspondance.apercu.reglesIncompletes')}
        </p>
      ) : null}
      {erreur ? (
        <p className={reprise.erreur} role="alert">
          {erreur}
        </p>
      ) : null}

      {apercu ? (
        <p className={reprise.discret}>
          {t('correspondance.apercu.source', {
            lus: apercu.lus,
            filtrees: apercu.filtres,
          })}
        </p>
      ) : null}

      {natures.map(([nature, sortie]) => (
        <div key={nature} className={reprise.pile}>
          <h4 className={reprise.sousTitre}>{t(`reprise.natures.${nature}`)}</h4>
          <dl className={styles.stats}>
            <div>
              <dt>{t('correspondance.apercu.produites')}</dt>
              <dd>{sortie.lignes.length}</dd>
            </div>
            <div>
              <dt>{t('correspondance.apercu.doublons')}</dt>
              <dd>{sortie.doublons}</dd>
            </div>
            <div>
              <dt>{t('correspondance.apercu.vides')}</dt>
              <dd>{sortie.vides}</dd>
            </div>
            <div>
              <dt>{t('correspondance.apercu.filtrees')}</dt>
              <dd>{sortie.filtres}</dd>
            </div>
            <div data-refus={sortie.refusees > 0 ? 'true' : undefined}>
              <dt>{t('correspondance.apercu.refusees')}</dt>
              <dd>{sortie.refusees}</dd>
            </div>
          </dl>
          {sortie.lignes.length > 0 ? (
            <TableauDefilant
              libelle={t('correspondance.apercu.tableau', {
                nature: t(`reprise.natures.${nature}`),
              })}
            >
              <table className={`${tableStyles.table} ${styles.tableApercu}`}>
                <thead>
                  <tr>
                    {sortie.colonnes.map((colonne) => (
                      <th key={colonne.cle} scope="col">
                        {colonne.entete}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortie.lignes.slice(0, LIGNES_AFFICHEES).map((ligne, rang) => (
                    <tr key={rang}>
                      {sortie.colonnes.map((colonne) => (
                        <td key={colonne.cle}>{ligne[colonne.cle] ?? ''}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableauDefilant>
          ) : (
            <p className={reprise.discret}>{t('correspondance.apercu.aucuneLigne')}</p>
          )}
        </div>
      ))}

      {apercu && apercu.erreurs.length > 0 ? (
        <div className={reprise.pile}>
          <h4 className={reprise.sousTitre}>
            {t('correspondance.apercu.erreurs', { count: apercu.erreurs.length })}
          </h4>
          <ul className={styles.erreursLignes}>
            {apercu.erreurs.slice(0, ERREURS_AFFICHEES).map((erreurLigne, rang) => (
              <li key={rang}>
                <span className={styles.erreurPosition}>
                  {t('correspondance.apercu.ligneSource', { ligne: erreurLigne.ligne })}
                  {erreurLigne.colonne ? ` · ${erreurLigne.colonne}` : ''}
                </span>
                <span>{erreurLigne.message}</span>
              </li>
            ))}
          </ul>
          {apercu.erreurs.length > ERREURS_AFFICHEES ? (
            <p className={reprise.discret}>
              {t('correspondance.apercu.erreursTronquees', { count: ERREURS_AFFICHEES })}
            </p>
          ) : null}
        </div>
      ) : null}
      {apercu && apercu.avertissements.length > 0 ? (
        <div className={reprise.pile}>
          <h4 className={reprise.sousTitre}>
            {t('correspondance.apercu.avertissements', { count: apercu.avertissements.length })}
          </h4>
          <ul className={styles.erreursLignes} data-ton="avertissement">
            {apercu.avertissements.slice(0, ERREURS_AFFICHEES).map((avertissement, rang) => (
              <li key={rang}>
                <span className={styles.erreurPosition}>
                  {t('correspondance.apercu.ligneSource', { ligne: avertissement.ligne })}
                </span>
                <span>{avertissement.message}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
